import React from 'react';
import { Circle, Award, CheckCircle2, Package } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { mockData } from '../mock';

const iconMap = { Circle, Award, Package };

const Products = ({ onRequestQuote }) => {
  return (
    <section id="products" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Premium Products
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Farm-fresh eggs delivered daily with guaranteed quality. Choose from our range of premium products tailored for your needs.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {mockData.products.map((product) => {
            const Icon = iconMap[product.icon];
            return (
              <div 
                key={product.id}
                className="bg-gradient-to-b from-white to-emerald-50/50 rounded-2xl overflow-hidden shadow-lg border-2 border-emerald-100 hover:border-emerald-300 transition-all hover:shadow-2xl group"
              >
                {/* Product Header */}
                <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 p-8 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-white/10 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-4 relative z-10 group-hover:scale-110 transition-transform">
                    <Icon className="text-white" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2 relative z-10">
                    {product.name}
                  </h3>
                  <Badge className="bg-white/20 backdrop-blur-md text-white border-white/30 relative z-10">
                    {product.grade}
                  </Badge>
                </div>

                {/* Product Body */}
                <div className="p-6">
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {product.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    {product.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle2 className="text-emerald-600 flex-shrink-0" size={18} />
                        <span className="text-gray-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Sizes */}
                  <div className="mb-6">
                    <div className="text-sm text-gray-500 mb-2">Available Sizes:</div>
                    <div className="flex flex-wrap gap-2">
                      {product.sizes.map((size, index) => (
                        <Badge key={index} variant="outline" className="border-emerald-600 text-emerald-700">
                          {size}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Min Order */}
                  <div className="text-sm text-gray-600 mb-4 bg-emerald-50 rounded-lg p-3">
                    <span className="font-semibold">Min. Order:</span> {product.minOrder}
                  </div>

                  {/* CTA Button */}
                  <Button 
                    onClick={onRequestQuote}
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                  >
                    Get Quote
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-emerald-700 to-emerald-900 rounded-2xl p-8 md:p-12 text-center text-white shadow-xl">
          <h3 className="text-3xl font-bold mb-4">
            Bulk Orders & Custom Requirements?
          </h3>
          <p className="text-emerald-100 text-lg mb-6 max-w-2xl mx-auto">
            We cater to restaurants, hotels, supermarkets, and distributors. Contact us for special pricing on bulk orders and customized delivery schedules.
          </p>
          <Button 
            onClick={onRequestQuote}
            size="lg"
            className="bg-white text-emerald-700 hover:bg-emerald-50 px-8"
          >
            Request Custom Quote
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Products;
